import type { SubmitEvent } from 'react';
import { useCallback, useState } from 'react';
import { AlertCircle, ArrowLeft, Lock } from 'lucide-react';

import { useCompleteRequiredPasswordUpdate, useLogin } from '@/api/endpoints/auth';
import { usePasswordPolicy } from '@/hooks/use-password-policy';
import { PasswordRequirements } from '@/modus/PasswordRequirements';
import { Button } from '@/ui/Button';
import { InputNext } from '@/ui/InputNext';
import { extractHTTPError } from '@/utils/http-client';

type SetNewPasswordFormProps = {
  username: string;
  changePasswordToken: string;
  redirectTo?: string;
  onCancel: () => void;
};

export function SetNewPasswordForm({ username, changePasswordToken, redirectTo, onCancel }: SetNewPasswordFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { isPasswordValid } = usePasswordPolicy();
  const [completePasswordUpdate, { isPending: isUpdating }] = useCompleteRequiredPasswordUpdate();
  const [login, { isPending: isLoggingIn }] = useLogin();

  const isLoading = isUpdating || isLoggingIn;
  const passwordValid = isPasswordValid(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const canSubmit = passwordValid && passwordsMatch && confirmPassword.length > 0 && !isLoading;

  const handleSubmit = useCallback(
    async (e: SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError(null);

      if (!passwordValid || !passwordsMatch) {
        return;
      }

      const { error: updateError } = await completePasswordUpdate({
        change_password_token: changePasswordToken,
        new_password: newPassword,
      });

      if (updateError) {
        setError(extractHTTPError(updateError, 'Failed to update password.'));
        return;
      }

      const { data: loginData, error: loginError } = await login({
        username,
        password: newPassword,
        redirect_to: redirectTo,
      });

      if (loginError) {
        setError(extractHTTPError(loginError, 'Password updated, but sign-in failed. Please sign in again.'));
        return;
      }

      if (loginData?.password_update_required) {
        setError('Password updated, but the account still requires action. Please sign in again.');
      }
    },
    [
      changePasswordToken,
      completePasswordUpdate,
      login,
      newPassword,
      passwordValid,
      passwordsMatch,
      redirectTo,
      username,
    ],
  );

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-medium text-white">Set a new password</h2>
        <p className="text-xs text-white/60">
          Your administrator requires you to update your password before signing in.
        </p>
      </div>

      {error && (
        <div className="flex w-full items-center gap-2 rounded-md bg-red-500/10 px-3 py-2 text-red-500">
          <AlertCircle className="size-4 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <InputNext
        id="new_password"
        name="new_password"
        type="password"
        autoComplete="new-password"
        autoFocus
        className="bg-white/3"
        startAdornment={<Lock className="size-4 text-muted-foreground" />}
        placeholder="new password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={isLoading}
        required
      />
      <InputNext
        id="confirm_password"
        name="confirm_password"
        type="password"
        autoComplete="new-password"
        className="bg-white/3"
        startAdornment={<Lock className="size-4 text-muted-foreground" />}
        placeholder="confirm new password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={isLoading}
        required
      />

      {showMismatch && <p className="text-xs text-red-400">Passwords do not match.</p>}

      <PasswordRequirements password={newPassword} />

      <Button type="submit" disabled={!canSubmit} loading={isLoading} className="w-full">
        Update password &amp; sign in
      </Button>

      <Button
        type="button"
        variant="link"
        Icon={ArrowLeft}
        className="text-sm text-white/70"
        disabled={isLoading}
        onClick={onCancel}
      >
        Back to sign in
      </Button>
    </form>
  );
}
